import React, { useMemo } from "react";
import { Alert, Descriptions, Drawer, Empty, Spin, Table, Tabs, Timeline, Typography } from "antd";
import dayjs from "dayjs";

const statusTextMap = {
    PENDING_CONFIRM: "待确认",
    CONFIRMED: "已生效",
    REJECTED: "已驳回",
};

const sourceTextMap = {
    MANUAL: "手工录入",
    CRAWLER_AUTO: "自动抓取",
    OCR_AUTO: "图片解析",
    MANUAL_PATCH: "人工修正",
};

const statusBadgeStyleMap = {
    PENDING_CONFIRM: {
        color: "#d46b08",
        background: "#fff7e6",
        border: "1px solid #ffd591",
    },
    CONFIRMED: {
        color: "#389e0d",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
    },
    REJECTED: {
        color: "#595959",
        background: "#fafafa",
        border: "1px solid #d9d9d9",
    },
};

const sanitizeHtmlFragment = (htmlFragment) => {
    if (!htmlFragment) {
        return "";
    }
    return htmlFragment
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, "");
};

const escapeHtmlAttr = (value) => String(value || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
const unescapeHtmlAttr = (value) =>
    String(value || "")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">");

const addAssetMapping = (mapping, key, resolvedUrl) => {
    if (!key || !resolvedUrl) {
        return;
    }
    const normalizedKey = unescapeHtmlAttr(key);
    mapping[key] = resolvedUrl;
    mapping[normalizedKey] = resolvedUrl;
    mapping[escapeHtmlAttr(normalizedKey)] = resolvedUrl;
};

const buildSnapshotAssetMapping = (rawPayload) => {
    const manifest = rawPayload?.snapshot_manifest;
    const assets = Array.isArray(manifest?.assets) ? manifest.assets : [];
    return assets.reduce((mapping, asset) => {
        const resolvedUrl = asset?.absolute_src || asset?.url;
        addAssetMapping(mapping, asset?.src, resolvedUrl);
        addAssetMapping(mapping, asset?.absolute_src, resolvedUrl);
        return mapping;
    }, {});
};

const rewriteFragmentAssetUrls = (htmlFragment, assetMapping) => {
    if (!htmlFragment || !assetMapping || Object.keys(assetMapping).length === 0) {
        return htmlFragment;
    }

    const rewritten = htmlFragment.replace(/((?:src|data-src)=["'])([^"']+)(["'])/gi, (match, prefix, currentSrc, suffix) => {
        const replacement = assetMapping[currentSrc] || assetMapping[unescapeHtmlAttr(currentSrc)];
        return replacement ? `${prefix}${escapeHtmlAttr(replacement)}${suffix}` : match;
    });

    return rewritten.replace(/(srcset=["'])([^"']+)(["'])/gi, (match, prefix, currentSrcset, suffix) => {
        const candidates = currentSrcset
            .split(",")
            .map((candidate) => {
                const values = candidate.trim().split(/\s+/).filter(Boolean);
                if (!values.length) {
                    return "";
                }
                const replacement = assetMapping[values[0]] || assetMapping[unescapeHtmlAttr(values[0])] || values[0];
                return [escapeHtmlAttr(replacement), ...values.slice(1)].join(" ");
            })
            .filter(Boolean);
        return candidates.length ? `${prefix}${candidates.join(", ")}${suffix}` : match;
    });
};

const renderMoney = (value) => (value == null ? "—" : Number(value).toFixed(2));
const renderPercent = (value) => (value == null ? "—" : `${Number(value).toFixed(2)}%`);
const hasNumericValue = (value) => value != null && Number.isFinite(Number(value));
const renderOptionalNumber = (value, digits = 2, suffix = "") => (hasNumericValue(value) ? `${Number(value).toFixed(digits)}${suffix}` : "—");
const renderDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : value;
};

const formatDisplayValue = (value) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const parsed = dayjs(value);
        if (parsed.isValid()) {
            return parsed.format("YYYY-MM-DD HH:mm:ss");
        }
    }
    if (Array.isArray(value)) {
        return value.map((item) => formatDisplayValue(item));
    }
    if (value && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, formatDisplayValue(item)]));
    }
    return value;
};

const renderJsonText = (value) => {
    if (!value) {
        return "—";
    }
    return JSON.stringify(formatDisplayValue(value), null, 2);
};

const chartRowDefinitions = [
    { key: "jet_fuel_price", label: "Jet fuel price" },
    { key: "asia_oceania", label: "Asia & Oceania" },
    { key: "europe_cis", label: "Europe & CIS" },
    { key: "middle_east", label: "Middle East" },
    { key: "north_america", label: "North America" },
    { key: "latin_america_caribbean", label: "Latin America & Caribbean" },
    { key: "africa", label: "Africa" },
    { key: "oil_price_dated_brent", label: "Oil Price (Dated Brent)" },
    { key: "crack_spread", label: "Crack Spread" },
];

const rowHasPriceData = (row) =>
    [
        row.share_in_global_index_pct,
        row.cts_per_gal,
        row.usd_per_bbl,
        row.usd_per_t,
        row.index_value,
        row.vs_prior_week_pct,
        row.vs_prior_month_pct,
        row.vs_prior_year_pct,
    ].some(hasNumericValue);

const buildFallbackPriceRows = (record) => {
    const rows = [
        {
            key: "global",
            label: "Jet fuel price",
            usd_per_t: record?.global_price_per_mt,
            usd_per_bbl: record?.global_price_per_bbl,
        },
        {
            key: "north_america",
            label: "North America",
            usd_per_t: record?.north_america_price_per_mt,
        },
        {
            key: "europe",
            label: "Europe",
            usd_per_t: record?.europe_price_per_mt,
        },
        {
            key: "asia_oceania",
            label: "Asia & Oceania",
            usd_per_t: record?.asia_oceania_price_per_mt,
        },
    ];
    return rows.filter(rowHasPriceData);
};

const buildChartPriceRows = (record) => {
    const modelOutput = record?.raw_payload?.chart_parse?.model_output || {};
    const chartRows = chartRowDefinitions
        .map(({ key, label }) => ({
            key,
            label,
            ...(modelOutput[key] || {}),
        }))
        .filter(rowHasPriceData);

    return chartRows.length ? chartRows : buildFallbackPriceRows(record);
};

const priceTableColumns = [
    {
        title: "指标",
        dataIndex: "label",
        key: "label",
        fixed: "left",
        width: 190,
    },
    {
        title: "Share",
        dataIndex: "share_in_global_index_pct",
        key: "share_in_global_index_pct",
        align: "right",
        width: 90,
        render: (value) => renderOptionalNumber(value, 1, "%"),
    },
    {
        title: "cts/gal",
        dataIndex: "cts_per_gal",
        key: "cts_per_gal",
        align: "right",
        width: 100,
        render: (value) => renderOptionalNumber(value),
    },
    {
        title: "$/bbl",
        dataIndex: "usd_per_bbl",
        key: "usd_per_bbl",
        align: "right",
        width: 100,
        render: (value) => renderOptionalNumber(value),
    },
    {
        title: "$/t",
        dataIndex: "usd_per_t",
        key: "usd_per_t",
        align: "right",
        width: 100,
        render: (value) => renderOptionalNumber(value),
    },
    {
        title: "Index",
        dataIndex: "index_value",
        key: "index_value",
        align: "right",
        width: 90,
        render: (value) => renderOptionalNumber(value, 1),
    },
    {
        title: "vs 上周",
        dataIndex: "vs_prior_week_pct",
        key: "vs_prior_week_pct",
        align: "right",
        width: 95,
        render: (value) => renderOptionalNumber(value, 1, "%"),
    },
    {
        title: "vs 上月",
        dataIndex: "vs_prior_month_pct",
        key: "vs_prior_month_pct",
        align: "right",
        width: 95,
        render: (value) => renderOptionalNumber(value, 1, "%"),
    },
    {
        title: "vs 上年",
        dataIndex: "vs_prior_year_pct",
        key: "vs_prior_year_pct",
        align: "right",
        width: 95,
        render: (value) => renderOptionalNumber(value, 1, "%"),
    },
];

const renderStatusBadge = (status) => {
    const style = statusBadgeStyleMap[status] || statusBadgeStyleMap.REJECTED;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 72,
                height: 28,
                padding: "0 12px",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 500,
                ...style,
            }}
        >
            {statusTextMap[status] || status}
        </span>
    );
};

const operationTextMap = {
    RECORD_CREATED: "创建记录",
    RECORD_UPDATED: "更新记录",
    STATUS_CONFIRMED: "确认生效",
    STATUS_REJECTED: "驳回记录",
};

const FuelPriceDetailDrawer = ({ open, record, loading, logs, logsLoading, originalPageHtml, originalPageLoading, onClose }) => {
    const priceRows = useMemo(() => buildChartPriceRows(record), [record]);
    const fallbackIframeDoc = useMemo(() => {
        if (!record?.layout_column_middle_html) {
            return "";
        }
        const assetMapping = buildSnapshotAssetMapping(record.raw_payload);
        const safeHtml = rewriteFragmentAssetUrls(sanitizeHtmlFragment(record.layout_column_middle_html), assetMapping);
        const baseTag = record.source_url ? `<base href="${escapeHtmlAttr(record.source_url)}" />` : "";
        return `<!DOCTYPE html><html><head><meta charset="utf-8" />${baseTag}<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:16px;line-height:1.6;color:#111;background:#fff}img{max-width:100%;height:auto}table{max-width:100%;border-collapse:collapse}td,th{border:1px solid #d9d9d9;padding:6px 8px}</style></head><body>${safeHtml}</body></html>`;
    }, [record?.layout_column_middle_html, record?.raw_payload, record?.source_url]);

    return (
        <Drawer
            title={record ? `油价记录详情 - ${record.week_end_date}` : "油价记录详情"}
            open={open}
            onClose={onClose}
            width={900}
            destroyOnHidden
        >
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
                    <Spin />
                </div>
            ) : !record ? (
                <Empty description="暂无详情数据" />
            ) : (
                <Tabs
                    items={[
                        {
                            key: "base",
                            label: "基础信息",
                            children: (
                                <>
                                    <Descriptions bordered column={2} size="small">
                                        <Descriptions.Item label="记录编号">{record.id}</Descriptions.Item>
                                        <Descriptions.Item label="来源">{sourceTextMap[record.source_type] || record.source_type}</Descriptions.Item>
                                        <Descriptions.Item label="状态">{renderStatusBadge(record.status)}</Descriptions.Item>
                                        <Descriptions.Item label="周结束日期">{record.week_end_date}</Descriptions.Item>
                                        <Descriptions.Item label="发布日期">{record.publish_date || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="抓取时间">{renderDateTime(record.source_captured_at)}</Descriptions.Item>
                                        <Descriptions.Item label="解析置信度">{renderPercent(record.parse_confidence)}</Descriptions.Item>
                                        <Descriptions.Item label="确认信息">
                                            {record.confirmed_by?.username ? `${record.confirmed_by.username} / ${renderDateTime(record.confirmed_at)}` : "未确认"}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Global $/MT">{renderMoney(record.global_price_per_mt)}</Descriptions.Item>
                                        <Descriptions.Item label="Global $/bbl">{renderMoney(record.global_price_per_bbl)}</Descriptions.Item>
                                        <Descriptions.Item label="创建时间">{renderDateTime(record.created_at)}</Descriptions.Item>
                                        <Descriptions.Item label="更新时间">{renderDateTime(record.updated_at)}</Descriptions.Item>
                                        {record.remark ? <Descriptions.Item label="备注" span={2}>{record.remark}</Descriptions.Item> : null}
                                    </Descriptions>
                                    <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                                        IATA 图表解析明细
                                    </Typography.Title>
                                    <Table
                                        size="small"
                                        bordered
                                        rowKey="key"
                                        columns={priceTableColumns}
                                        dataSource={priceRows}
                                        pagination={false}
                                        scroll={{ x: 960 }}
                                        locale={{ emptyText: "暂无图表解析明细" }}
                                    />
                                </>
                            ),
                        },
                        {
                            key: "preview",
                            label: "页面原样展示",
                            children: originalPageLoading ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                                    <Spin />
                                </div>
                            ) : record.layout_column_middle_html ? (
                                <>
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="这是抓取时保存的历史片段，不保证与当前 IATA 在线页面实时一致。"
                                        style={{ marginBottom: 16 }}
                                    />
                                    <iframe
                                        title="IATA layout-column-middle preview"
                                        sandbox=""
                                        srcDoc={originalPageHtml || fallbackIframeDoc}
                                        style={{ width: "100%", minHeight: 520, border: "1px solid #f0f0f0", borderRadius: 8 }}
                                    />
                                </>
                            ) : (
                                <Empty description="该记录没有可展示的 HTML 片段" />
                            ),
                        },
                        {
                            key: "raw",
                            label: "原始证据",
                            children: (
                                <>
                                    <Descriptions bordered column={1} size="small">
                                        <Descriptions.Item label="来源 URL">{record.source_url || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="HTML 校验摘要">{record.layout_html_checksum || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="解析置信度">{record.parse_confidence || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="HTML 纯文本">
                                            <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                                                {record.layout_column_middle_text || "—"}
                                            </Typography.Paragraph>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="原始 Payload">
                                            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                                                {renderJsonText(record.raw_payload)}
                                            </pre>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </>
                            ),
                        },
                        {
                            key: "logs",
                            label: "审计日志",
                            children: logsLoading ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                                    <Spin />
                                </div>
                            ) : logs?.length ? (
                                <Timeline
                                    items={logs.map((item) => ({
                                        color: item.operation_type === "STATUS_REJECTED" ? "red" : item.operation_type === "STATUS_CONFIRMED" ? "green" : "blue",
                                        children: (
                                            <div>
                                                <Typography.Text strong>{operationTextMap[item.operation_type] || item.operation_type}</Typography.Text>
                                                <div style={{ marginTop: 4, color: "rgba(0,0,0,0.45)", fontSize: 12 }}>
                                                    {item.operator_name || "系统"} · {renderDateTime(item.created_at)}
                                                </div>
                                                {item.description ? (
                                                    <Typography.Paragraph style={{ marginTop: 8, marginBottom: 8 }}>
                                                        {item.description}
                                                    </Typography.Paragraph>
                                                ) : null}
                                                {item.after_snapshot ? (
                                                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", background: "#fafafa", padding: 12, borderRadius: 6 }}>
                                                        {renderJsonText(item.after_snapshot)}
                                                    </pre>
                                                ) : null}
                                            </div>
                                        ),
                                    }))}
                                />
                            ) : (
                                <Empty description="暂无审计日志" />
                            ),
                        },
                    ]}
                />
            )}
        </Drawer>
    );
};

export default FuelPriceDetailDrawer;
